import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  side?: 'left' | 'right' | 'top' | 'bottom';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className,
  side = 'left',
  size = 'md',
  showCloseButton = false,
}) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const drawerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Use viewport-based widths on mobile, fixed widths on desktop
  // Using !important to override Sheet component's default widths
  const sizeClasses = {
    sm: isMobile ? '!w-full !max-w-full' : '!w-[300px]',
    md: isMobile ? '!w-full !max-w-full' : '!w-[400px]',
    lg: isMobile ? '!w-full !max-w-full' : '!w-[500px]',
    xl: isMobile ? '!w-full !max-w-full' : '!w-[600px]',
    full: '!w-full !max-w-full',
  };

  // Reset position when opening/closing
  useEffect(() => {
    if (isOpen) {
      setTranslateX(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Don't start dragging if touching the close button
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;

    if (side !== 'left' && side !== 'right') return;

    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || (side !== 'left' && side !== 'right')) return;

    e.preventDefault();
    const newX = e.touches[0].clientX;
    setCurrentX(newX);

    const deltaX = newX - startX;
    let newTranslateX = 0;

    if (side === 'left') {
      // For left side, negative delta means swiping left (closing)
      newTranslateX = Math.min(0, deltaX);
    } else {
      // For right side, positive delta means swiping right (closing)
      newTranslateX = Math.max(0, -deltaX);
    }

    setTranslateX(newTranslateX);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);
    const deltaX = currentX - startX;
    const threshold = 100; // Minimum swipe distance to trigger close

    if (side === 'left' && deltaX < -threshold) {
      // Swipe left on left drawer = close
      onClose();
    } else if (side === 'right' && deltaX > threshold) {
      // Swipe right on right drawer = close
      onClose();
    } else {
      // Reset position if swipe wasn't far enough
      setTranslateX(0);
    }
  };

  const getTransformStyle = () => {
    if (side === 'left') {
      return {
        transform: `translateX(${translateX}px)`,
        opacity: isDragging ? Math.max(0.7, 1 - -translateX / 200) : 1,
      };
    } else if (side === 'right') {
      return {
        transform: `translateX(${-translateX}px)`,
        opacity: isDragging ? Math.max(0.7, 1 - translateX / 200) : 1,
      };
    }
    return {};
  };

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side={side}
        className={cn(
          'h-full p-0 transition-transform duration-200 ease-out',
          sizeClasses[size],
          side === 'left' && 'left-0',
          side === 'right' && 'right-0',
          isMobile && '!max-w-[100vw]', // Ensure drawer never exceeds viewport
          className
        )}
        ref={drawerRef}
        style={getTransformStyle()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        title={title || t('common.drawer.title_fallback')}
        description={
          title
            ? t('common.drawer.description_with_title', { title })
            : t('common.drawer.description_fallback')
        }
      >
        {title && (
          <SheetHeader className="border-b border-border bg-transparent p-4">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-lg font-semibold text-foreground">
                {title}
              </SheetTitle>
            </div>
          </SheetHeader>
        )}

        {/* Custom close button positioned on the right side */}
        {showCloseButton && (
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 sm:right-4 sm:top-4 z-50 p-2 rounded-lg bg-muted dark:bg-card"
            aria-label={t('common.drawer.close_label')}
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground hover:text-foreground dark:hover:text-foreground" />
          </Button>
        )}

        <div className="relative h-full flex-1 overflow-y-auto bg-transparent">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileDrawer;
